import type { ReactNode } from 'react';

/**
 * Markdown-lite for bot messages: **bold**, _italic_, `code`, [label](url), "- " bullet lines, "1. "
 * numbered lines (steps) and blank-line paragraphs. Nothing else is interpreted, and no HTML is ever
 * produced from the text: everything becomes React elements, so a message can't inject markup.
 */
export function Markdown({ text }: { text: string }) {
  return <div className="md">{blocks(text)}</div>;
}

/** Only web and mail links; anything else (javascript:, data:) shows as plain text. */
export function safeUrl(url: string): string | null {
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  try {
    const parsed = new URL(url);
    return ['https:', 'http:', 'mailto:', 'tel:'].includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
}

function blocks(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const paragraphs = text.replace(/\r\n?/g, '\n').split(/\n[ \t]*\n/);
  paragraphs.forEach((paragraph, p) => {
    const lines = paragraph.split('\n');
    let plain: string[] = [];
    let bullets: string[] = [];
    let steps: string[] = [];
    const flushPlain = () => {
      if (!plain.length) return;
      const key = `p${p}-${out.length}`;
      out.push(
        <p key={key}>
          {plain.flatMap((line, i) => [
            // biome-ignore lint/suspicious/noArrayIndexKey: lines of one paragraph, in order
            ...(i ? [<br key={`br${i}`} />] : []),
            ...inline(line, `${key}-${i}`),
          ])}
        </p>,
      );
      plain = [];
    };
    const flushBullets = () => {
      if (!bullets.length) return;
      const key = `ul${p}-${out.length}`;
      out.push(
        <ul key={key}>
          {bullets.map((item, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: list items in order
            <li key={i}>{inline(item, `${key}-${i}`)}</li>
          ))}
        </ul>,
      );
      bullets = [];
    };
    const flushSteps = () => {
      if (!steps.length) return;
      const key = `ol${p}-${out.length}`;
      out.push(
        <ol key={key}>
          {steps.map((item, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: steps in order
            <li key={i}>{inline(item, `${key}-${i}`)}</li>
          ))}
        </ol>,
      );
      steps = [];
    };
    for (const line of lines) {
      const bullet = /^\s*[-•]\s+(.*)$/.exec(line);
      const step = /^\s*\d{1,2}[.)]\s+(.*)$/.exec(line);
      if (bullet) {
        flushPlain();
        flushSteps();
        bullets.push(bullet[1] ?? '');
      } else if (step) {
        flushPlain();
        flushBullets();
        steps.push(step[1] ?? '');
      } else if (line.trim()) {
        flushBullets();
        flushSteps();
        plain.push(line);
      }
    }
    flushPlain();
    flushBullets();
    flushSteps();
  });
  return out;
}

const LINK = /^\[([^\]\n]+)\]\(([^)\s]+)\)/;
const CODE = /^`([^`\n]+)`/;

/** Where a [label](url) or `code` that starts at `i` ends, so markers inside them are skipped. */
function atomEnd(text: string, i: number): number {
  const rest = text.slice(i);
  const match = LINK.exec(rest) ?? CODE.exec(rest);
  return match ? i + match[0].length : i;
}

/** The closing marker for one opened at `from`, outside links and code. -1 if there's none. */
function closingMarker(text: string, from: number, marker: '**' | '_'): number {
  let i = from;
  while (i < text.length) {
    const skip = atomEnd(text, i);
    if (skip > i) {
      i = skip;
      continue;
    }
    if (text.startsWith(marker, i) && i > from) {
      const after = text[i + marker.length] ?? '';
      // An underscore inside a word (snake_case) isn't the end of italics.
      if (marker === '**' || !/[\p{L}\p{N}]/u.test(after)) return i;
    }
    i += 1;
  }
  return -1;
}

function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  let plain = '';
  let i = 0;
  const flush = () => {
    if (plain) out.push(plain);
    plain = '';
  };
  while (i < text.length) {
    const rest = text.slice(i);
    const code = CODE.exec(rest);
    if (code) {
      flush();
      out.push(<code key={`${key}c${i}`}>{code[1]}</code>);
      i += code[0].length;
      continue;
    }
    const link = LINK.exec(rest);
    if (link) {
      flush();
      const url = safeUrl(link[2] ?? '');
      const label = inline(link[1] ?? '', `${key}l${i}`);
      out.push(
        url ? (
          <a key={`${key}a${i}`} href={url} target="_blank" rel="noopener noreferrer">
            {label}
          </a>
        ) : (
          <span key={`${key}a${i}`}>{label}</span>
        ),
      );
      i += link[0].length;
      continue;
    }
    if (rest.startsWith('**')) {
      const end = closingMarker(text, i + 2, '**');
      if (end > i + 2) {
        flush();
        out.push(<strong key={`${key}b${i}`}>{inline(text.slice(i + 2, end), `${key}b${i}`)}</strong>);
        i = end + 2;
        continue;
      }
    }
    const before = i === 0 ? ' ' : (text[i - 1] ?? ' ');
    if (rest.startsWith('_') && !/[\p{L}\p{N}]/u.test(before)) {
      const end = closingMarker(text, i + 1, '_');
      if (end > i + 1) {
        flush();
        out.push(<em key={`${key}i${i}`}>{inline(text.slice(i + 1, end), `${key}i${i}`)}</em>);
        i = end + 1;
        continue;
      }
    }
    plain += text[i];
    i += 1;
  }
  flush();
  return out;
}
